import { defaultExportedChart, defineChart } from '..'
import { isNumber } from '@vueuse/core'
import { regression } from 'echarts-stat'
import { tooltipWithTitle } from '@/composables/useChart/theme'
import type { Values } from '@/composables/useValues'

type Params = [Values, Values]

export default defineChart<Params>(({ sorted, formatter }, comparison) => {
    if (!sorted) return

    const { isPrevalence } = useFramework()

    /**
     * List of scatter data points.
     */
    const scatterData = sorted
        .map(item => {
            const data = comparison.data?.find(({ iso }) => iso === item.iso)

            const value = data?.[comparison.valueKey]

            if (!isNumber(value)) return undefined as never

            return {
                name: item.iso,
                country: item.iso,
                iso: item.iso,
                value: [item.value, value]
            }
        })
        .filter(Boolean)

    /**
     * Get the points and expression
     * of the regression.
     */
    const { points } = regression(
        'linear',
        scatterData.map(({ value }) => value),
        1
    )

    /**
     * GDP formatted.
     */
    const gdpFormatter = formatNumber({
        currency: 'USD',
        style: 'currency',
        compactDisplay: 'short',
        notation: 'standard',
        maximumFractionDigits: 0
    })

    /**
     * Whether to set the axiss to be logarithmic.
     */
    const displayLogAxis =
        isPrevalence(comparison.dimension) && !comparison.isAbsolute

    return {
        tooltip: {},
        grid: {
            left: comparison.isAbsolute ? '10%' : '5%',
            bottom: '15%'
        },
        xAxis: {
            show: true,
            type: 'value',
            name: 'GDP per capita PPP (current international $)',
            nameRotate: 0,
            position: 'bottom',
            nameGap: 30,
            nameTruncate: {
                maxWidth: undefined
            },
            splitLine: {
                show: true
            },
            axisLabel: {
                formatter
            }
        },
        yAxis: {
            show: true,
            type: displayLogAxis ? 'log' : 'value',
            min: displayLogAxis ? 0.1 : 0,
            max: displayLogAxis ? 100 : undefined,
            nameGap: comparison.isAbsolute ? 100 : 50,
            name: comparison.dimension?.name.toUpperCase() || '',
            splitLine: {
                show: false
            },
            axisLabel: {
                show: true,
                formatter(value: number, index: number) {
                    if (index === 0) return ''
                    return formatter(value)
                }
            }
        },
        series: [
            {
                type: 'scatter',
                tooltip: {
                    formatter(params) {
                        const { value, country } = params.data as {
                            value: number[]
                            country: string
                        }
                        return tooltipWithTitle(
                            gdpFormatter(value[0]),
                            country,
                            formatter(value[1])
                        )
                    }
                },
                label: {
                    show: false
                },
                data: scatterData
            },
            {
                type: 'line',
                smooth: true,
                symbolSize: 0,
                lineStyle: {
                    color: 'black'
                },
                labelLayout: {
                    x: '50%',
                    y: '0%',
                    align: 'right'
                },
                data: points,
                silent: true
            }
        ],
        export: defaultExportedChart()
    }
})
